import { useEffect, useState } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Modal } from './modals';
import { Caption, Button } from './primitives';
import { hasOnboarded, markOnboarded, type OnboardingSlide } from './onboardingStorage';

/**
 * OnboardingModal — modal d'accueil 1ère visite par module (MVP2.5 T7, bug A8).
 *
 * Spec : `docs/WAKEUP_MVP2.5.md §2 T7`. Léon découvre un nouveau module pour
 * la 1ère fois → on lui montre 2 slides illustrées (texte + ASCII art board
 * annoté) qui répondent à : « C'est quoi ce module ? » et « Qu'est-ce que je
 * dois faire concrètement ? ».
 *
 * Persistance : localStorage `tcc_onboarded_<slug>` (via helpers
 * `./onboardingStorage`). Une fois vu, plus jamais réaffiché (sauf reset
 * manuel via DevTools). 5 modules, 5 slugs distincts : `mission-control`,
 * `opening-lab`, `tactics-dojo`, `endgame-forge`, `game-autopsy`.
 *
 * Anti-cibles (PRODUCT §5.4 + WAKEUP §9) : pas de gamification, pas de bouton
 * « passer » qui marquerait l'onboarding comme « non-vu » — soit Léon clique
 * « C'est parti » et c'est marqué vu, soit il ferme la croix et c'est marqué
 * vu aussi (one-shot deal — on ne le re-pénible jamais).
 */

export interface OnboardingModalProps {
  moduleSlug: string;
  moduleLabel: string;
  tone: 'violet' | 'cyan' | 'amber' | 'coral' | 'acid';
  slides: OnboardingSlide[];
  /**
   * Force l'affichage même si localStorage indique « déjà vu ». Utile pour
   * un futur bouton « Revoir l'aide » (hors scope T7 mais le composant le
   * permet sans refacto).
   */
  forceShow?: boolean;
  onClose?: () => void;
}

const TONE_TEXT: Record<OnboardingModalProps['tone'], string> = {
  violet: 'text-violet-500',
  cyan: 'text-cyan-500',
  amber: 'text-amber-500',
  coral: 'text-coral-500',
  acid: 'text-acid-500',
};

const TONE_DOT: Record<OnboardingModalProps['tone'], string> = {
  violet: 'bg-violet-500',
  cyan: 'bg-cyan-500',
  amber: 'bg-amber-500',
  coral: 'bg-coral-500',
  acid: 'bg-acid-500',
};

export function OnboardingModal({
  moduleSlug,
  moduleLabel,
  tone,
  slides,
  forceShow = false,
  onClose,
}: OnboardingModalProps): JSX.Element | null {
  const [open, setOpen] = useState<boolean>(() => forceShow || !hasOnboarded(moduleSlug));
  const [index, setIndex] = useState(0);

  // Si le slug change en cours (navigation), recalculer l'état d'affichage.
  useEffect(() => {
    setOpen(forceShow || !hasOnboarded(moduleSlug));
    setIndex(0);
  }, [moduleSlug, forceShow]);

  function handleClose(): void {
    markOnboarded(moduleSlug);
    setOpen(false);
    onClose?.();
  }

  if (slides.length === 0) return null;
  const lastIndex = slides.length - 1;
  // Index clamp défensif : si forceShow re-déclenche un slide retiré, on borne.
  const safeIndex = Math.min(index, lastIndex);
  const slide = slides[safeIndex];
  if (!slide) return null;
  const isLast = safeIndex === lastIndex;
  const isFirst = safeIndex === 0;
  const accentButton = tone === 'acid' ? undefined : tone;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      ariaLabel={`Bienvenue dans ${moduleLabel}`}
      maxWidth={560}
    >
      <Caption color={tone}>Bienvenue · {moduleLabel}</Caption>
      <h2 className={`font-display text-heading-2 mt-1 mb-3 ${TONE_TEXT[tone]}`}>
        {slide.title}
      </h2>

      {slide.visual && (
        <div
          className="mb-4 p-4 rounded-lg border border-ink-700 bg-ink-900 font-mono text-xs text-ink-300 leading-tight whitespace-pre overflow-x-auto"
          aria-hidden="true"
          data-testid="onboarding-visual"
        >
          {slide.visual}
        </div>
      )}

      <div className="text-body-sm text-paper-50 leading-relaxed">{slide.body}</div>

      {/* Indicateurs slides */}
      <div className="mt-5 flex items-center justify-center gap-2" aria-hidden="true">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-150 ${
              i === safeIndex ? `w-6 ${TONE_DOT[tone]}` : 'w-1.5 bg-ink-700'
            }`}
          />
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="default"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={isFirst}
        >
          <CaretLeft size={14} aria-hidden="true" />
          Précédent
        </Button>
        {isLast ? (
          <Button
            variant="primary"
            size="default"
            {...(accentButton ? { accent: accentButton } : {})}
            onClick={handleClose}
          >
            C'est parti
          </Button>
        ) : (
          <Button
            variant="primary"
            size="default"
            {...(accentButton ? { accent: accentButton } : {})}
            onClick={() => setIndex((i) => Math.min(lastIndex, i + 1))}
          >
            Suivant
            <CaretRight size={14} aria-hidden="true" />
          </Button>
        )}
      </div>
    </Modal>
  );
}
